import type { MemberListItem } from "@albertine/types";
import { z } from "zod";
import type { Dictionary } from "../../../common/types/Dictionary";
import { takeIfAnyStringMatches } from "../../../utils/filter.util";

export const RequestFilter = z.enum([
    "activeRequests",
    "noActiveRequests",
    "noRequestsYet",
    "hasHadRequests",
    "noBookingsYet",
]);
export type RequestFilter = z.infer<typeof RequestFilter>;
export const RequestFilterMap: Record<RequestFilter, string> = {
    activeRequests: "Active requests",
    noActiveRequests: "No active requests",
    noRequestsYet: "No requests yet",
    hasHadRequests: "Has had some requests",
    noBookingsYet: "No bookings yet",
};

export type FilterByRequest = {
    id: RequestFilter;
    name: string;
    count: number;
};

function isConfirmedBookingExists(member: MemberListItem) {
    return member.confirmedBookingsCount > 0;
}

function isAnyRequestExists(member: MemberListItem) {
    return member.totalRequests > 0;
}

function isActiveRequestExists(member: MemberListItem) {
    return member.activeRequests > 0;
}

export function filterMembersByRequest(
    requestFilters: string[],
    members: MemberListItem[],
) {
    const evalFilterGroup = (member: MemberListItem) => {
        const result: string[] = [];
        if (!isActiveRequestExists(member)) {
            result.push("noActiveRequests");
        }
        if (isActiveRequestExists(member)) {
            result.push("activeRequests");
        }
        if (!isAnyRequestExists(member)) {
            result.push("noRequestsYet");
        }
        if (isAnyRequestExists(member)) {
            result.push("hasHadRequests");
        }
        if (!isConfirmedBookingExists(member)) {
            result.push("noBookingsYet");
        }
        return result;
    };

    return takeIfAnyStringMatches<MemberListItem>(
        members,
        (member) => evalFilterGroup(member),
        requestFilters,
    );
}

export function getRequestCounts(members: MemberListItem[]): FilterByRequest[] {
    const dict: Dictionary<number> = {};
    RequestFilter.options.forEach((option) => {
        dict[option] = 0;
    });

    let noActiveRequestsCount = 0;
    let activeRequestsCount = 0;
    let noRequestsYetCount = 0;
    let noBookingsYetCount = 0;
    let hasHadRequestsCount = 0;
    members.forEach((member) => {
        noBookingsYetCount += !isConfirmedBookingExists(member) ? 1 : 0;
        noRequestsYetCount += !isAnyRequestExists(member) ? 1 : 0;
        hasHadRequestsCount += isAnyRequestExists(member) ? 1 : 0;
        activeRequestsCount += isActiveRequestExists(member) ? 1 : 0;
        noActiveRequestsCount += !isActiveRequestExists(member) ? 1 : 0;
    });
    if (noBookingsYetCount > 0) {
        dict.noBookingsYet = noBookingsYetCount;
    }
    if (noRequestsYetCount > 0) {
        dict.noRequestsYet = noRequestsYetCount;
    }
    if (activeRequestsCount > 0) {
        dict.activeRequests = activeRequestsCount;
    }
    if (hasHadRequestsCount > 0) {
        dict.hasHadRequests = hasHadRequestsCount;
    }
    if (noActiveRequestsCount > 0) {
        dict.noActiveRequests = noActiveRequestsCount;
    }

    return RequestFilter.options.map((option) => ({
        id: option,
        name: RequestFilterMap[option],
        count: dict[option],
    }));
}
